import { useState, useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
export const usePlacemarkClustering = ({ viewer, placemarks, clusterThresholdPx = 40, enabled, isItemVisible }) => {
    const [clusters, setClusters] = useState([]);
    const [singlePlacemarks, setSinglePlacemarks] = useState([]);
    const [activeSpider, setActiveSpider] = useState(null);
    const clusterEntitiesRef = useRef(new Map());
    const clusterDataSourceRef = useRef(null);
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const dataSource = new Cesium.CustomDataSource('placemark-clusters');
        viewer.dataSources.add(dataSource);
        clusterDataSourceRef.current = dataSource;
        return () => {
            if (viewer && !viewer.isDestroyed() && clusterDataSourceRef.current) {
                viewer.dataSources.remove(clusterDataSourceRef.current);
                clusterDataSourceRef.current = null;
            }
            clusterEntitiesRef.current.clear();
        };
    }, [viewer]);
    const createClusterBadgeCanvas = useCallback((count) => {
        const canvas = document.createElement('canvas');
        const size = 32;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(50, 50, 50, 0.9)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = 'white';
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(count.toString(), size / 2, size / 2);
        return canvas;
    }, []);
    const updateClusters = useCallback(() => {
        if (!viewer || viewer.isDestroyed() || !enabled) {
            setClusters([]);
            setSinglePlacemarks([...placemarks.keys()]);
            return;
        }
        const scene = viewer.scene;
        const placemarkScreenPositions = new Map();
        placemarks.forEach((placemark, id) => {
            if (!isItemVisible(id))
                return;
            const screenPos = Cesium.SceneTransforms.worldToWindowCoordinates(scene, placemark.position);
            if (screenPos) {
                placemarkScreenPositions.set(id, { x: screenPos.x, y: screenPos.y });
            }
        });
        const clustered = new Set();
        const newClusters = [];
        const newSingles = [];
        const ids = Array.from(placemarkScreenPositions.keys());
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            if (clustered.has(id))
                continue;
            const pos = placemarkScreenPositions.get(id);
            const clusterMembers = [id];
            for (let j = i + 1; j < ids.length; j++) {
                const otherId = ids[j];
                if (clustered.has(otherId))
                    continue;
                const otherPos = placemarkScreenPositions.get(otherId);
                const dx = pos.x - otherPos.x;
                const dy = pos.y - otherPos.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                if (distance < clusterThresholdPx) {
                    clusterMembers.push(otherId);
                }
            }
            if (clusterMembers.length >= 2) {
                clusterMembers.forEach(memberId => clustered.add(memberId));
                let avgX = 0, avgY = 0;
                let avgLat = 0, avgLng = 0;
                clusterMembers.forEach(memberId => {
                    const screenPos = placemarkScreenPositions.get(memberId);
                    avgX += screenPos.x;
                    avgY += screenPos.y;
                    const pm = placemarks.get(memberId);
                    const cart = Cesium.Cartographic.fromCartesian(pm.position);
                    avgLat += Cesium.Math.toDegrees(cart.latitude);
                    avgLng += Cesium.Math.toDegrees(cart.longitude);
                });
                avgX /= clusterMembers.length;
                avgY /= clusterMembers.length;
                avgLat /= clusterMembers.length;
                avgLng /= clusterMembers.length;
                newClusters.push({
                    id: `cluster-${clusterMembers[0]}`,
                    placemarkIds: clusterMembers,
                    screenPosition: { x: avgX, y: avgY },
                    worldPosition: { lat: avgLat, lng: avgLng }
                });
            }
            else {
                newSingles.push(id);
            }
        }
        setClusters(newClusters);
        setSinglePlacemarks(newSingles);
        if (clusterDataSourceRef.current) {
            clusterDataSourceRef.current.entities.removeAll();
            clusterEntitiesRef.current.clear();
            newClusters.forEach(cluster => {
                const entity = clusterDataSourceRef.current.entities.add({
                    position: Cesium.Cartesian3.fromDegrees(cluster.worldPosition.lng, cluster.worldPosition.lat),
                    billboard: {
                        image: createClusterBadgeCanvas(cluster.placemarkIds.length),
                        verticalOrigin: Cesium.VerticalOrigin.CENTER,
                        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                        disableDepthTestDistance: Number.POSITIVE_INFINITY,
                        scale: 1.0
                    }
                });
                clusterEntitiesRef.current.set(cluster.id, entity);
            });
        }
        placemarks.forEach((placemark, id) => {
            if (!isItemVisible(id))
                return;
            const isInCluster = clustered.has(id);
            if (placemark.entity)
                placemark.entity.show = !isInCluster;
            // showLabel === false hides the label regardless of cluster state;
            // undefined/true keeps the existing "show when not clustered" behaviour.
            const labelAllowed = placemark.showLabel !== false;
            if (placemark.labelEntity)
                placemark.labelEntity.show = !isInCluster && labelAllowed;
        });
    }, [viewer, placemarks, clusterThresholdPx, enabled, createClusterBadgeCanvas, activeSpider, isItemVisible]);
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const removeListener = viewer.camera.moveEnd.addEventListener(() => {
            if (activeSpider) {
                setActiveSpider(null);
            }
            updateClusters();
        });
        updateClusters();
        return () => {
            removeListener();
        };
    }, [viewer, updateClusters, activeSpider]);
    useEffect(() => {
        updateClusters();
    }, [placemarks.size, updateClusters]);
    const handleClusterClick = useCallback((clusterId, screenPosition) => {
        const cluster = clusters.find(c => c.id === clusterId);
        if (!cluster)
            return false;
        const items = cluster.placemarkIds.map(id => {
            const placemark = placemarks.get(id);
            return {
                id,
                label: placemark.name,
                color: placemark.color,
                icon: 'placemark'
            };
        });
        setActiveSpider({
            clusterId,
            items,
            position: screenPosition
        });
        const clusterEntity = clusterEntitiesRef.current.get(clusterId);
        if (clusterEntity) {
            clusterEntity.show = false;
        }
        return true;
    }, [clusters, placemarks]);
    const checkClusterEntityClick = useCallback((entity, screenPosition) => {
        for (const [clusterId, clusterEntity] of clusterEntitiesRef.current.entries()) {
            if (entity === clusterEntity) {
                return handleClusterClick(clusterId, screenPosition);
            }
        }
        return false;
    }, [handleClusterClick]);
    const handleSpiderSelect = useCallback((placemarkId) => {
        setActiveSpider(null);
        return placemarkId;
    }, []);
    const dismissSpider = useCallback(() => {
        if (activeSpider) {
            const clusterEntity = clusterEntitiesRef.current.get(activeSpider.clusterId);
            if (clusterEntity) {
                clusterEntity.show = true;
            }
        }
        setActiveSpider(null);
    }, [activeSpider]);
    return {
        clusters,
        singlePlacemarks,
        activeSpider,
        updateClusters,
        handleClusterClick: checkClusterEntityClick,
        handleSpiderSelect,
        dismissSpider,
        clusterEntities: clusterEntitiesRef.current
    };
};
